'use client'

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { App, Button, Image, Input, Space, Upload } from 'antd'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import {
  getWebsiteFaviconConfig,
  getWebsiteLogoConfig,
  getWebsiteTitleConfig,
  updateWebsiteConfig,
} from '@/service/config'

/** 网站标题 / Logo / Favicon，与后端 `/web_config`（超级管理员）一致 */
export default function WebsiteSettingPanel() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [websiteTitle, setWebsiteTitle] = useState('')
  const [titleError, setTitleError] = useState('')
  const [logo, setLogo] = useState('')
  const [logoLoading, setLogoLoading] = useState(false)
  const [favicon, setFavicon] = useState('')
  const [faviconLoading, setFaviconLoading] = useState(false)

  useEffect(() => {
    getWebsiteTitleConfig().then(res => {
      setWebsiteTitle(res?.value || '')
    })
    getWebsiteLogoConfig().then(res => {
      setLogo(res?.value || '')
    })
    getWebsiteFaviconConfig().then(res => {
      setFavicon(res?.value || '')
    })
  }, [])

  const validateWebsiteTitle = (title: string): string => {
    if (!title || title.trim().length === 0)
      return t('common.websiteSetting.titleRequired')

    const trimmedTitle = title.trim()

    if (trimmedTitle.length < 2)
      return t('common.websiteSetting.titleMinLength')

    if (trimmedTitle.length > 50)
      return t('common.websiteSetting.titleMaxLength')

    const invalidChars = /[<>"&]/g
    if (invalidChars.test(trimmedTitle))
      return t('common.websiteSetting.titleInvalidChars')

    if (/^[\s\-_.]+$/.test(trimmedTitle))
      return t('common.websiteSetting.titleInvalidPattern')

    return ''
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setWebsiteTitle(value)
    setTitleError(validateWebsiteTitle(value))
  }

  const handleSaveWebsiteTitle = () => {
    const error = validateWebsiteTitle(websiteTitle)
    if (error) {
      setTitleError(error)
      message.error(error)
      return
    }

    setTitleError('')

    updateWebsiteConfig({
      key: 'title',
      value: websiteTitle.trim(),
    })
      .then(() => {
        message.success(t('common.websiteSetting.titleSaveSuccess'))
      })
      .catch(() => {
        message.error(t('common.websiteSetting.saveFailed'))
      })
  }

  const handleLogoUpload = (file: File) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error(t('common.websiteSetting.imageOnly'))
      return false
    }

    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error(t('common.websiteSetting.imageSizeLimit'))
      return false
    }

    setLogoLoading(true)

    const reader = new FileReader()
    reader.onload = e => {
      const base64 = e.target?.result as string
      setLogo(base64)
      setLogoLoading(false)
      message.success(t('common.websiteSetting.logoUploadSuccess'))
    }
    reader.onerror = () => {
      setLogoLoading(false)
      message.error(t('common.websiteSetting.imageReadFailed'))
    }
    reader.readAsDataURL(file)

    return false
  }

  const handleSaveLogo = () => {
    if (!logo) {
      message.error(t('common.websiteSetting.logoRequired'))
      return
    }

    setLogoLoading(true)
    updateWebsiteConfig({
      key: 'logo',
      value: logo,
    })
      .then(() => {
        message.success(t('common.websiteSetting.logoSaveSuccess'))
        setLogoLoading(false)
      })
      .catch(() => {
        message.error(t('common.websiteSetting.saveFailed'))
        setLogoLoading(false)
      })
  }

  const handleDeleteLogo = () => {
    setLogo('')
    message.success(t('common.websiteSetting.logoDeleted'))
  }

  const handleFaviconUpload = (file: File) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error(t('common.websiteSetting.imageOnly'))
      return false
    }

    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error(t('common.websiteSetting.imageSizeLimit'))
      return false
    }

    setFaviconLoading(true)

    const reader = new FileReader()
    reader.onload = e => {
      const base64 = e.target?.result as string
      setFavicon(base64)
      setFaviconLoading(false)
      message.success(t('common.websiteSetting.faviconUploadSuccess'))
    }
    reader.onerror = () => {
      setFaviconLoading(false)
      message.error(t('common.websiteSetting.imageReadFailed'))
    }
    reader.readAsDataURL(file)

    return false
  }

  const handleSaveFavicon = () => {
    if (!favicon) {
      message.error(t('common.websiteSetting.faviconRequired'))
      return
    }

    setFaviconLoading(true)
    updateWebsiteConfig({
      key: 'favicon',
      value: favicon,
    })
      .then(() => {
        message.success(t('common.websiteSetting.faviconSaveSuccess'))
        setFaviconLoading(false)
      })
      .catch(() => {
        message.error(t('common.websiteSetting.saveFailed'))
        setFaviconLoading(false)
      })
  }

  const handleDeleteFavicon = () => {
    setFavicon('')
    message.success(t('common.websiteSetting.faviconDeleted'))
  }

  return (
    <Space direction='vertical' size={32} className='w-full'>
      <div className='flex flex-col gap-2'>
        <label htmlFor='site_title' className='text-sm'>
          {t('common.websiteSetting.titleSetting')}
        </label>
        <div className='flex flex-row flex-wrap gap-5'>
          <Input
            placeholder={t('common.websiteSetting.titlePlaceholder') as string | undefined}
            id='site_title'
            className='w-80 max-w-full'
            value={websiteTitle}
            onChange={handleTitleChange}
            status={titleError ? 'error' : ''}
          />
          <Button
            type='primary'
            className='w-20'
            onClick={handleSaveWebsiteTitle}>
            {t('common.websiteSetting.save')}
          </Button>
        </div>
        {titleError && (
          <div className='mt-1 text-xs text-red-500'>{titleError}</div>
        )}
        <div className='text-xs text-gray-400'>
          {t('common.websiteSetting.titleTip')}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <label className='text-sm'>{t('common.websiteSetting.logoSetting')}</label>
        <div className='flex flex-row flex-wrap gap-4 items-center'>
          <Upload
            beforeUpload={handleLogoUpload}
            showUploadList={false}
            accept='image/*'>
            <Button
              className='w-40'
              icon={<UploadOutlined />}
              loading={logoLoading}>
              {t('common.websiteSetting.logoUpload')}
            </Button>
          </Upload>

          {logo && (
            <>
              <div className='flex flex-col gap-2'>
                <div className='text-xs text-gray-500'>{t('common.websiteSetting.logoPreview')}</div>
                <Image
                  src={logo}
                  alt={t('common.websiteSetting.logoAlt') as string | undefined}
                  width={32}
                  height={32}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <Button
                type='primary'
                onClick={handleSaveLogo}
                loading={logoLoading}>
                {t('common.websiteSetting.logoSave')}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteLogo}>
                {t('common.websiteSetting.logoDelete')}
              </Button>
            </>
          )}
        </div>
        <div className='text-xs text-gray-400'>
          {t('common.websiteSetting.logoTip')}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <label className='text-sm'>{t('common.websiteSetting.faviconSetting')}</label>
        <div className='flex flex-row flex-wrap gap-4 items-center'>
          <Upload
            beforeUpload={handleFaviconUpload}
            showUploadList={false}
            accept='image/*'>
            <Button
              className='w-40'
              icon={<UploadOutlined />}
              loading={faviconLoading}>
              {t('common.websiteSetting.faviconUpload')}
            </Button>
          </Upload>

          {favicon && (
            <>
              <div className='flex flex-col gap-2'>
                <div className='text-xs text-gray-500'>{t('common.websiteSetting.logoPreview')}</div>
                <Image
                  src={favicon}
                  alt={t('common.websiteSetting.faviconAlt') as string | undefined}
                  width={32}
                  height={32}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <Button
                type='primary'
                onClick={handleSaveFavicon}
                loading={faviconLoading}>
                {t('common.websiteSetting.faviconSave')}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteFavicon}>
                {t('common.websiteSetting.faviconDelete')}
              </Button>
            </>
          )}
        </div>
        <div className='text-xs text-gray-400'>
          {t('common.websiteSetting.faviconTip')}
        </div>
      </div>
    </Space>
  )
}
